import React from 'react'

import {
  createWaitlistEntry,
  deleteWaitlistEntry,
  fetchWaitlist,
  searchApartmentParents,
  updateWaitlistEntry
} from 'api/apartment'

import ApartmentApiCrudPage from '../shared/ApartmentApiCrudPage'
import { useSelectedApartment } from '../shared/apartmentStore'

// Every subject the backend knows (oh_apartments.constants.ProgramName), not
// just the four the waitlist used to offer. A family can wait for any subject
// the org teaches, in any building — the waitlist is demand, not supply, so it
// is deliberately NOT narrowed to the programs a given apartment runs today.
const PROGRAMS = [
  'Art',
  'Art & Design',
  'Public Speaking',
  'Robotics',
  'Dance',
  'Music',
  'STEM',
  'Language'
]

const columns = [
  { name: 'position', label: '#', options: { filter: false } },
  { name: 'child_name', label: 'Child' },
  { name: 'parent_name', label: 'Parent' },
  { name: 'phone', label: 'Phone', options: { filter: false } },
  { name: 'program', label: 'Program' },
  { name: 'status', label: 'Status' },
  { name: 'notes', label: 'Notes', options: { filter: false } }
]

const ApartmentWaitlist = () => {
  const [apartment] = useSelectedApartment()
  // A child is picked through their parent, so the child options depend on the
  // parent currently selected in the form.
  const [children, setChildren] = React.useState([])
  const [initialParent, setInitialParent] = React.useState(null)

  const fetchParents = React.useCallback(
    (text) => searchApartmentParents(apartment.id, text),
    [apartment.id]
  )

  const fields = [
    {
      // Parents and children are main-system users, added by an admin first —
      // never typed in here. The name and phone columns are written from these.
      name: 'parent',
      label: 'Parent (search by name or number)',
      type: 'autocomplete',
      fetchValues: fetchParents,
      initialValue: initialParent,
      onSelect: (opt) => setChildren(opt?.children || [])
    },
    {
      name: 'child',
      label: 'Child (of the selected parent)',
      type: 'select',
      options: children.map((c) => ({ value: c.uuid, label: c.name }))
    },
    { name: 'program', label: 'Program', type: 'select', options: PROGRAMS },
    // Position is first-come-first-served — assigned automatically per program.
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: ['Waiting', 'Offered', 'Enrolled', 'Cancelled']
    },
    // Why they are waiting, what was promised, when to call back.
    { name: 'notes', label: 'Notes', type: 'textarea' }
  ]

  const rowToForm = (row) => ({
    ...row,
    parent: row.parentPerson?.uuid || '',
    child: row.childPerson?.uuid || ''
  })

  // Opening the modal is what primes the parent box and its children — doing it
  // in rowToForm would be a setState during render.
  const onModalOpen = React.useCallback(
    async (row) => {
      setInitialParent(row?.parentPerson || null)
      if (!row?.parentPerson) {
        setChildren([])
        return
      }
      const results = await fetchParents(row.parentPerson.name)
      const match = results.find((p) => p.uuid === row.parentPerson.uuid)
      setChildren(match?.children || [])
    },
    [fetchParents]
  )

  // `parent_name`, `child_name` and `phone` are read-only server-side, written
  // from the FKs.
  const formToPayload = (values) => ({
    ...values,
    parent: values.parent || null,
    child: values.child || null
  })

  const fetchList = React.useCallback(
    () => fetchWaitlist(apartment.id),
    [apartment.id]
  )
  const createItem = React.useCallback(
    (payload) =>
      createWaitlistEntry({ ...payload, apartmentSlug: apartment.id }),
    [apartment.id]
  )

  return (
    <ApartmentApiCrudPage
      title="Waitlist"
      tableTitle="Waitlist"
      entityLabel="Entry"
      fetchList={fetchList}
      createItem={createItem}
      updateItem={updateWaitlistEntry}
      deleteItem={deleteWaitlistEntry}
      columns={columns}
      fields={fields}
      rowToForm={rowToForm}
      onModalOpen={onModalOpen}
      formToPayload={formToPayload}
      refreshKey={apartment.id}
      emptyValues={{
        parent: '',
        child: '',
        program: 'Art',
        status: 'Waiting'
      }}
    />
  )
}

export default ApartmentWaitlist
